import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_TAGS } from '@/shared/api';
import {
  addEmployeesToGroup,
  createEmployeeGroup,
  deleteEmployeeGroup,
  fetchEmployeeGroupById,
  fetchEmployeeGroups,
  removeEmployeesFromGroup,
  updateEmployeeGroup,
} from './api';

/**
 * Query keys для групп операторов.
 */
export const employeeGroupKeys = {
  all: [API_TAGS.EMPLOYEE_GROUPS] as const,
  lists: () => [...employeeGroupKeys.all, 'list'] as const,
  list: (filters: { name?: string; limit?: number; offset?: number } = {}) =>
    [...employeeGroupKeys.lists(), { ...filters }] as const,
  details: () => [...employeeGroupKeys.all, 'detail'] as const,
  detail: (id: number) => [...employeeGroupKeys.details(), id] as const,
};

const LIMIT = 50;

/**
 * Бесконечная пагинация списка групп операторов с поиском по названию.
 */
export const useInfiniteEmployeeGroups = (params: { name?: string; limit?: number } = {}) => {
  const limit = params.limit ?? LIMIT;
  const query = useInfiniteQuery({
    queryKey: employeeGroupKeys.list({ name: params.name, limit }),
    queryFn: ({ pageParam }) =>
      fetchEmployeeGroups({ name: params.name || undefined, limit, offset: pageParam * limit }),
    initialPageParam: 0,
    placeholderData: keepPreviousData,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      const loaded = limit * (lastPageParam + 1);
      return (lastPage.total ?? 0) <= loaded ? undefined : lastPageParam + 1;
    },
  });

  const data = {
    total: query.data?.pages[0]?.total,
    groups: query.data?.pages.flatMap((p) => p.groups) ?? [],
  };

  return { ...query, data };
};

/**
 * Получение конкретной группы операторов по идентификатору.
 */
export const useEmployeeGroup = (id?: number) =>
  useQuery({
    queryKey: employeeGroupKeys.detail(id!),
    queryFn: () => fetchEmployeeGroupById(id!),
    enabled: !!id,
  });

/**
 * Создание группы операторов.
 */
export const useCreateEmployeeGroup = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createEmployeeGroup,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeeGroupKeys.lists() });
    },
  });
};

/**
 * Обновление группы операторов.
 */
export const useUpdateEmployeeGroup = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateEmployeeGroup,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: employeeGroupKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: employeeGroupKeys.lists() });
    },
  });
};

/**
 * Удаление группы операторов.
 */
export const useDeleteEmployeeGroup = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteEmployeeGroup,
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: employeeGroupKeys.lists() });
      queryClient.removeQueries({ queryKey: employeeGroupKeys.detail(id) });
    },
  });
};

/**
 * Добавление операторов в группу.
 */
export const useAddEmployeesToGroup = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addEmployeesToGroup,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: employeeGroupKeys.detail(variables.id) });
    },
  });
};

/**
 * Исключение операторов из группы.
 */
export const useRemoveEmployeesFromGroup = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: removeEmployeesFromGroup,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: employeeGroupKeys.detail(variables.id) });
    },
  });
};
